import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { useEmojis, BASELINE_EMOJIS } from '../context/EmojiContext';

// Re-export for legacy imports (some pages import FAVOURITE_EMOJIS directly).
export const FAVOURITE_EMOJIS = BASELINE_EMOJIS;

/**
 * Small inline emoji picker. Renders a compact button showing the current emoji
 * (or a placeholder) and opens a portal popover with a keyboard of favourites
 * plus a text field so any emoji can be pasted.
 */
export const EmojiPicker = ({ value, onChange, size = 'md', testId = 'emoji-picker' }) => {
  const { emojis, saveEmoji } = useEmojis();
  const [open, setOpen] = useState(false);
  const [anchor, setAnchor] = useState(null);
  const [custom, setCustom] = useState('');
  const [portalTarget, setPortalTarget] = useState(null);
  const btnRef = React.useRef(null);

  const openPicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      const W = 288, H = 320, pad = 8;  // popover ~ w-72; keep within viewport
      let left = r.left;
      if (left + W > window.innerWidth - pad) left = Math.max(pad, window.innerWidth - W - pad);
      let top = r.bottom + 4;
      if (top + H > window.innerHeight - pad) top = Math.max(pad, r.top - H - 4);  // flip above
      setAnchor({ top, left });
      const dialog = btnRef.current.closest('[role="dialog"]');
      setPortalTarget(dialog || document.body);
    }
    setOpen((o) => !o);
  };
  const pick = (v) => {
    const clean = (v || '').trim();
    onChange(clean);
    setOpen(false); setCustom('');
  };
  const pickAndRemember = (v) => {
    const clean = (v || '').trim();
    if (clean) saveEmoji(clean);
    pick(clean);
  };

  const sizeCls = size === 'sm'
    ? 'w-8 h-8 text-lg'
    : size === 'lg'
      ? 'w-14 h-14 text-3xl'
      : 'w-10 h-10 text-2xl';

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={openPicker}
        className={`${sizeCls} inline-flex items-center justify-center rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100 hover:border-pink-300 transition`}
        title="pick an emoji"
        data-testid={testId}
      >
        {value || <span className="text-slate-300 text-xs">+ emoji</span>}
      </button>
      {open && anchor && createPortal(
        <div data-emoji-ui="true">
          <div className="fixed inset-0 z-[9998]" style={{ pointerEvents: 'auto' }} onClick={() => setOpen(false)} />
          <div
            className="fixed z-[9999] w-72 bg-white rounded-lg shadow-xl border border-slate-200 p-3"
            style={{ top: anchor.top, left: anchor.left, pointerEvents: 'auto' }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400">favourites</div>
              {value && (
                <button
                  type="button"
                  onClick={() => pick('')}
                  className="text-[10px] text-slate-400 hover:text-red-600"
                  data-testid={`${testId}-clear`}
                >clear</button>
              )}
            </div>
            <div className="grid grid-cols-8 gap-1 mb-3 max-h-56 overflow-y-auto">
              {emojis.map((e) => (
                <button
                  type="button"
                  key={e}
                  onClick={() => pick(e)}
                  className={`w-8 h-8 rounded-md text-lg hover:bg-pink-50 transition-transform hover:scale-110 ${value === e ? 'bg-pink-100 ring-1 ring-pink-300' : ''}`}
                  data-testid={`${testId}-fav-${e}`}
                >{e}</button>
              ))}
            </div>
            <div className="border-t border-slate-100 pt-2">
              <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-1">or paste any emoji</div>
              <div className="flex items-center gap-2">
                <input
                  autoFocus
                  value={custom}
                  onChange={(e) => setCustom(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && custom.trim()) pickAndRemember(custom.trim()); }}
                  placeholder="🌟"
                  className="h-8 flex-1 text-lg text-center border border-slate-200 rounded outline-none focus:ring-2 focus:ring-pink-200"
                  data-testid={`${testId}-custom-input`}
                />
                <button
                  type="button"
                  onClick={() => { if (custom.trim()) pickAndRemember(custom.trim()); }}
                  disabled={!custom.trim()}
                  className="text-[11px] font-semibold text-white bg-pink-500 hover:bg-pink-600 disabled:opacity-50 rounded px-2 py-1"
                  data-testid={`${testId}-custom-save`}
                >save</button>
              </div>
            </div>
          </div>
        </div>,
        portalTarget || document.body,
      )}
    </>
  );
};

export default EmojiPicker;
